import { Schema } from "effect";
import { EgkAttestationHandle } from "./egk-attestation.schema";
import {
  EligibleContactType,
  EmailAssertion,
  HausarztAssertion,
  PhoneAssertion,
} from "./contact-assertion.schema";
import { ReviewItemId } from "./patient-info-review.schema";

/**
 * A patient-proposed contact value: either a telephone number or an e-mail address.
 *
 * The union is resolved by the operation's `type` at validation time; both members are format-gated
 * and neither asserts ownership.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { ContactAssertion } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(ContactAssertion)("anna@example.de");
 * ```
 */
export const ContactAssertion = Schema.Union([PhoneAssertion, EmailAssertion]);

/**
 * The decoded type of a {@link ContactAssertion}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ContactAssertion } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * function display(value: ContactAssertion): string {
 *   return value;
 * }
 * ```
 */
export type ContactAssertion = typeof ContactAssertion.Type;

/**
 * Edit an existing reviewable contact in place.
 *
 * `itemId` must be one the review offered. The server re-resolves it to a vendor ID through its own
 * capability map and re-checks that the contact is still ordinary, so a client cannot edit a
 * protected contact by asserting otherwise.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { EditContactOperation } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(EditContactOperation)({
 *   kind: "edit",
 *   itemId: "itm_7f3a9c1e",
 *   value: "+49 30 7654321",
 * });
 * ```
 */
export const EditContactOperation = Schema.Struct({
  kind: Schema.Literal("edit"),
  itemId: ReviewItemId,
  value: ContactAssertion,
});

/**
 * Delete an existing reviewable contact.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { DeleteContactOperation } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(DeleteContactOperation)({ kind: "delete", itemId: "itm_7f3a9c1e" });
 * ```
 */
export const DeleteContactOperation = Schema.Struct({
  kind: Schema.Literal("delete"),
  itemId: ReviewItemId,
});

/**
 * Add a new contact of one of the six eligible types.
 *
 * `clientItemId` is a client-generated key used only to correlate this proposal with its result;
 * it is not authority and the server never trusts it as an identifier of anything on file.
 *
 * A new contact is written with neutral defaults only — confirmation, portal, and preferred status
 * are never inherited from an existing contact, because the eGK does not prove the patient controls
 * the new value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { AddContactOperation } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(AddContactOperation)({
 *   kind: "add",
 *   clientItemId: "new-1",
 *   type: "EMAILPRIV",
 *   value: "anna@example.de",
 * });
 * ```
 */
export const AddContactOperation = Schema.Struct({
  kind: Schema.Literal("add"),
  clientItemId: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(64))),
  type: EligibleContactType,
  value: ContactAssertion,
});

/**
 * One proposed change to a contact.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { ContactOperation } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(ContactOperation)({ kind: "delete", itemId: "itm_7f3a9c1e" });
 * ```
 */
export const ContactOperation = Schema.Union(
  [EditContactOperation,
  DeleteContactOperation,
  AddContactOperation],
);

/**
 * The decoded type of a {@link ContactOperation}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { ContactOperation } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * function isAdd(operation: ContactOperation): boolean {
 *   return operation.kind === "add";
 * }
 * ```
 */
export type ContactOperation = typeof ContactOperation.Type;

/**
 * The patient's one save.
 *
 * `submissionId` is a stable client-generated identity for this logical save. A retry reuses it
 * verbatim so the server can recognise the same proposal and return its durable result instead of
 * repeating work; a *different* proposal under a spent capability is rejected outright.
 *
 * An empty `operations` array with no `hausarzt` property is the explicit "Alles korrekt" no-op: it
 * consumes the capability and performs zero Doc Cirrus writes and zero tasks. A present `hausarzt`
 * is always task-only: a string proposes free text and `null` proposes clearing the current entry.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoSubmitRequest } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * const noOp = Schema.decodeUnknownSync(PatientInfoSubmitRequest)({
 *   egkAttestation: "f3a9c1e0b7d2",
 *   submissionId: "sub-1",
 *   operations: [],
 * });
 * ```
 */
export const PatientInfoSubmitRequest = Schema.Struct({
  egkAttestation: EgkAttestationHandle,
  submissionId: Schema.String.pipe(Schema.check(Schema.isMinLength(1)), Schema.check(Schema.isMaxLength(64))),
  operations: Schema.Array(ContactOperation),
  hausarzt: Schema.optional(Schema.NullOr(HausarztAssertion)),
});

/**
 * The decoded type of a {@link PatientInfoSubmitRequest}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoSubmitRequest } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * function isNoOp(request: PatientInfoSubmitRequest): boolean {
 *   return request.operations.length === 0;
 * }
 * ```
 */
export type PatientInfoSubmitRequest = typeof PatientInfoSubmitRequest.Type;

/**
 * What durably became of one requested field.
 *
 * Four of the five are terminal:
 * - `updated` — the change is durable in Doc Cirrus.
 * - `satisfied` — Doc Cirrus already held exactly this value; nothing needed writing.
 * - `task-created` — a reception task carries the change; staff will apply it.
 * - `not-saved` — the change was not applied and no task carries it. The patient must be told
 *   plainly and directed to reception.
 *
 * `pending-retry` is **not** terminal and never a final answer. It may appear only while the
 * capability is still active with room for another attempt, and the client must present it as *not
 * yet saved* — never as saved or in progress. Before the capability expires the server converts any
 * remaining `pending-retry` action into a reception task, so no requested field can end without a
 * disposition the patient was told truthfully.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { FieldOutcome } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(FieldOutcome)("task-created");
 * ```
 */
export const FieldOutcome = Schema.Literals(
  ["updated",
  "satisfied",
  "task-created",
  "not-saved",
  "pending-retry"],
);

/**
 * The decoded type of a {@link FieldOutcome}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { FieldOutcome } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * const outcome: FieldOutcome = "updated";
 * ```
 */
export type FieldOutcome = typeof FieldOutcome.Type;

/**
 * Whether a {@link FieldOutcome} is a final disposition.
 *
 * `pending-retry` is the only non-terminal outcome. A capability may be consumed only once every
 * requested field is terminal.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { isTerminalOutcome } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * isTerminalOutcome("task-created"); // true
 * isTerminalOutcome("pending-retry"); // false
 * ```
 */
export function isTerminalOutcome(outcome: FieldOutcome): boolean {
  return outcome !== "pending-retry";
}

/**
 * The outcome of one requested field, keyed back to what the patient proposed.
 *
 * `itemId` identifies an edited or deleted contact; `clientItemId` identifies an added one; `field`
 * identifies the task-only Hausarzt assertion. Exactly one is present, so the client can place every
 * result next to the input that produced it without inventing an ordering.
 *
 * Internal reason codes, task markers, and vendor IDs are deliberately absent.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { FieldResult } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * Schema.decodeUnknownSync(FieldResult)({ itemId: "itm_7f3a9c1e", outcome: "updated" });
 * ```
 */
export const FieldResult = Schema.Struct({
  itemId: Schema.optional(ReviewItemId),
  clientItemId: Schema.optional(Schema.String),
  field: Schema.optional(Schema.Literal("hausarzt")),
  outcome: FieldOutcome,
});

/**
 * The decoded type of a {@link FieldResult}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { FieldResult } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * function isSaved(result: FieldResult): boolean {
 *   return result.outcome === "updated" || result.outcome === "satisfied";
 * }
 * ```
 */
export type FieldResult = typeof FieldResult.Type;

/**
 * The result of one save.
 *
 * `complete` is true only when every requested field reached a terminal outcome and the capability
 * was consumed. When false, at least one field is still `pending-retry` and the client may retry
 * with the identical `submissionId` and proposal while the capability remains active.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import { Schema } from "effect";
 * import { PatientInfoSubmitResult } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * const result = Schema.decodeUnknownSync(PatientInfoSubmitResult)({
 *   complete: true,
 *   results: [{ itemId: "itm_7f3a9c1e", outcome: "updated" }],
 * });
 * ```
 */
export const PatientInfoSubmitResult = Schema.Struct({
  complete: Schema.Boolean,
  results: Schema.Array(FieldResult),
});

/**
 * The decoded type of a {@link PatientInfoSubmitResult}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * import type { PatientInfoSubmitResult } from "@ascleo/frontdesk-contracts/patient-info/submit";
 *
 * function needsRetry(result: PatientInfoSubmitResult): boolean {
 *   return !result.complete;
 * }
 * ```
 */
export type PatientInfoSubmitResult = typeof PatientInfoSubmitResult.Type;
